/**
 * File utils/context/BookingDataProvider.js
 *
 * To manage the data of the booking selected
 * 
 * Learn more: https://saint-augustin.ch
 */

import {useState, createContext, useReducer, useEffect, useContext, useCallback} from 'react';
import {useFetch} from '../hooks/useFetch'

// Styles
import '../styles/BookingData.css'

const BookingDataContext = createContext();

const reducer = (state, action) => {
	switch (action.type) {
		case 'setLocations':
			return action.payload;

		case 'selectLocation': {
			const updated = [...state];
			if (updated[action.index]) {
				updated[action.index].isSelected = action.payload;
			}
			return updated;
		}

		case 'selectEquipment': {
			const updated = [...state];
			const loc = updated[action.index];
			if (loc && loc.equipments && loc.equipments[action.equIndex]) {
				loc.equipments[action.equIndex].isSelected = action.payload;
			}
			return updated;
		}

		default:
			return state;
	}
};

 const BookingDataProvider = ({children}) => {

	const [events, setEvents] = useState([]);
	const [nbPersons, setNbPersons] = useState(1);

	// Dates tools
	const formatDateForInput = (date) => {
		const pad = (n) => n.toString().padStart(2, '0');
		return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
	}

	const getDefaultDates = () => {
		const now = new Date();
		//const plus2Hours = new Date(now.getTime() + 2 * 60 * 60 * 1000);
		return {
			start: formatDateForInput(now),
			end: formatDateForInput(now),
		};
	};

	const {start, end} = getDefaultDates();
	const [beginBooking, setBeginBooking] = useState(start);
	const [endBooking, setEndBooking] = useState(end);
	const [disponibilityStatus, setDisponibilityStatus] = useState(false);
	
	// Get data from Wordpress Database
	const [locations, dispatch] = useReducer(reducer, []);
	const {data, isLoading, error} = useFetch('https://' + window.location.host + '/wp-json/wp/v2/locations');
	
	useEffect(() => {
		if (data) {
			const normalized = data.map((location) => ({
				...location,
				equipments: (location.equipments || []).map((equipment) => ({
					...equipment,
					isSelected: equipment.price == 0,
				})),
				isSelected: false,
				min_time_available: location.time_period_available?.min_time_available || null,
				max_time_available: location.time_period_available?.max_time_available || null,
			}));
			dispatch({ type: 'setLocations', payload: normalized });
		}
	}, [data]);

	useEffect(() => {
		if (new Date(endBooking) <= new Date(beginBooking)) {
			const newEnd = new Date(new Date(beginBooking).getTime() + 2 * 60 * 60 * 1000);
			setEndBooking(formatDateForInput(newEnd));
		}
	}, [beginBooking]);

	const selectLocation = useCallback((index, isSelected) => {
		dispatch({ type: 'selectLocation', index, payload: isSelected });
	}, []);

	const selectEquipment = useCallback((index, equIndex, isSelected) => {
		dispatch({ type: 'selectEquipment', index, equIndex, payload: isSelected });
	}, []);

	// Get Google events via endpoint WP
	useEffect(() => {
		fetch("/wp-json/booking/v1/events")
			.then(res => res.json())
			.then(data => {
				setEvents(data || []);
			})
			.catch(err => console.error("Erreur chargement événements:", err));
	}, []);

	return (
		<BookingDataContext.Provider
			value={{
				events,
				nbPersons,
				setNbPersons,
				beginBooking,
				setBeginBooking,
				endBooking,
				setEndBooking,
				locations,
				selectLocation,
				selectEquipment,
				isLoading,
				error,
				disponibilityStatus,
				setDisponibilityStatus
			}}
		>
			{children}
		</BookingDataContext.Provider>
	)
 }

 export const useBookingDataContext = () => {
	return useContext(BookingDataContext);
 }

 export default BookingDataProvider;